/**
 * O formulário da corretiva, na caixa de diálogo do SM.
 *
 * Papel: reproduz `#corrModalOverlay` (l. 641–671) e o que `openCorrModal`,
 * `saveCorrRecord` e `deleteCorrRecord` fazem (l. 1056–1102): os campos nas
 * cinco linhas da origem, "Cancelar", "Excluir" só na edição e "Salvar".
 *
 * Como usar: quem monta passa `corretiva` para editar ou `null` para criar, dá
 * ao componente uma `key` por registro, e o desmonta para fechar — `onFechar` é
 * chamado pelo "✕", pelo "Cancelar" e depois de gravar ou excluir. Sem a `key`,
 * a caixa de outro registro abriria com os valores do anterior.
 *
 * Não há `<form>`: Enter num campo não grava, como na origem, e só o clique em
 * "Salvar" grava. Nenhum campo é obrigatório e nenhum mostra erro. O resolver é
 * o Zod do contrato, que não exige campo; se ele recusar algo — um texto acima
 * de `LIMITE_DE_TEXTO_DA_MANUTENCAO` —, a notificação de erro aparece e a caixa
 * fica aberta. O custo vai como `Number` do campo, e o vazio como nulo; `0` é
 * custo, e abre como "0" na edição.
 *
 * Enquanto a gravação ou a exclusão não volta, "Salvar" e "Excluir" ficam
 * desabilitados, sem trocar o texto — e é isso que impede o segundo clique de
 * mandar um segundo `POST`. Na falha, a caixa fica aberta com o que foi
 * digitado, e a notificação diz "Não foi possível salvar." ou "Não foi possível
 * excluir.", sem repetir dado do registro.
 */
import { type Corretiva, novaCorretivaSchema } from '@concierge/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useId, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';

import { useNotificacao } from '@/shared/embutido';
import { useExcluirCorretiva, useSalvarCorretiva } from '../hooks/usar-manutencoes';
import { ORDEM_DOS_STATUS_CORRETIVA, ROTULOS_STATUS_CORRETIVA } from '../rotulos';
import { CORRETIVA_EM_BRANCO, type CorretivaNoFormulario } from '../types';
import { Botao } from './kit/botao';
import { CaixaDeDialogo } from './kit/caixa-de-dialogo';
import {
  CLASSE_DA_AREA_DE_TEXTO,
  CLASSE_DO_CONTROLE,
  CampoDoFormulario,
  LinhaDupla,
} from './kit/campo-do-formulario';

interface FormularioCorretivaProps {
  /** A corretiva a editar, ou `null` para criar. */
  corretiva: Corretiva | null;
  /** A casa da tela, dona da corretiva. */
  residenciaId: string;
  /** As áreas já usadas, para o `datalist` do campo Área. */
  areas: readonly string[];
  /** Fecha a caixa, desmontando o formulário. */
  onFechar: () => void;
}

/**
 * O custo como a origem o lê do campo.
 *
 * @param valor - O texto do `input type="number"`.
 * @returns `Number` do texto, ou `null` para o campo vazio.
 */
function custoDoCampo(valor: string): number | null {
  return valor === '' ? null : Number(valor);
}

/**
 * O formulário de uma corretiva.
 *
 * @param corretiva - O registro a editar, ou `null`.
 * @param residenciaId - A casa da tela.
 * @param areas - As sugestões de área.
 * @param onFechar - Chamado para fechar, depois de gravar ou excluir, ou pelo
 *   "✕" e pelo "Cancelar".
 * @returns A caixa com os campos.
 */
export function FormularioCorretiva({
  corretiva,
  residenciaId,
  areas,
  onFechar,
}: FormularioCorretivaProps) {
  const id = useId();
  const notificar = useNotificacao();
  const salvar = useSalvarCorretiva();
  const excluir = useExcluirCorretiva();
  const [ocupado, setOcupado] = useState(false);
  // "Cancelar" e "✕" seguem ativos durante a gravação; se ela volta depois do
  // formulário desmontado, `onFechar` fecharia a caixa que a pessoa abriu depois.
  const montado = useRef(true);

  useEffect(() => {
    montado.current = true;
    return () => {
      montado.current = false;
    };
  }, []);

  const form = useForm<CorretivaNoFormulario>({
    resolver: zodResolver(novaCorretivaSchema),
    defaultValues:
      corretiva === null
        ? { ...CORRETIVA_EM_BRANCO, residenciaId }
        : {
            residenciaId,
            area: corretiva.area ?? '',
            descricao: corretiva.descricao ?? '',
            status: corretiva.status,
            responsavel: corretiva.responsavel ?? '',
            atualizacao: corretiva.atualizacao ?? '',
            custo: corretiva.custo ?? '',
            observacao: corretiva.observacao ?? '',
          },
  });

  /**
   * Grava o que passou pelo resolver.
   *
   * @param dados - Os valores do formulário, já normalizados pelo Zod.
   */
  async function gravar(dados: CorretivaNoFormulario) {
    try {
      await salvar.mutateAsync({
        id: corretiva?.id ?? null,
        dados: novaCorretivaSchema.parse(dados),
      });
      notificar('✓ Registro salvo');
      if (montado.current) onFechar();
    } catch {
      notificar('Não foi possível salvar.', false);
      setOcupado(false);
    }
  }

  /** Avisa a recusa do resolver, sem fechar a caixa. */
  function recusar() {
    notificar('Não foi possível salvar.', false);
    setOcupado(false);
  }

  /** Grava, travando os dois botões até a resposta. */
  function handleSalvar() {
    setOcupado(true);
    void form.handleSubmit(gravar, recusar)();
  }

  /** Exclui sem perguntar, como `deleteCorrRecord`. */
  async function handleExcluir() {
    if (corretiva === null) return;
    setOcupado(true);
    try {
      await excluir.mutateAsync(corretiva.id);
      notificar('✓ Registro excluído');
      if (montado.current) onFechar();
    } catch {
      notificar('Não foi possível excluir.', false);
      setOcupado(false);
    }
  }

  return (
    <CaixaDeDialogo
      titulo={
        corretiva === null ? 'Novo registro — Corretivas' : 'Editar registro — Corretivas'
      }
      onFechar={onFechar}
      rodape={
        <>
          <Botao variante="fantasma" onClick={onFechar}>
            Cancelar
          </Botao>
          {corretiva !== null && (
            <Botao variante="perigo" disabled={ocupado} onClick={() => void handleExcluir()}>
              Excluir
            </Botao>
          )}
          <Botao variante="primario" disabled={ocupado} onClick={handleSalvar}>
            Salvar
          </Botao>
        </>
      }
    >
      <CampoDoFormulario rotulo="Área" id={`${id}-area`}>
        <input
          id={`${id}-area`}
          type="text"
          list={`${id}-areas`}
          className={CLASSE_DO_CONTROLE}
          {...form.register('area')}
        />
        <datalist id={`${id}-areas`}>
          {areas.map((area) => (
            <option key={area} value={area} />
          ))}
        </datalist>
      </CampoDoFormulario>
      <CampoDoFormulario rotulo="Descrição / Pendência" id={`${id}-descricao`}>
        <textarea
          id={`${id}-descricao`}
          className={CLASSE_DA_AREA_DE_TEXTO}
          {...form.register('descricao')}
        />
      </CampoDoFormulario>
      <LinhaDupla>
        <CampoDoFormulario rotulo="Status" id={`${id}-status`}>
          <select
            id={`${id}-status`}
            className={CLASSE_DO_CONTROLE}
            {...form.register('status')}
          >
            {ORDEM_DOS_STATUS_CORRETIVA.map((status) => (
              <option key={status} value={status}>
                {ROTULOS_STATUS_CORRETIVA[status]}
              </option>
            ))}
          </select>
        </CampoDoFormulario>
        <CampoDoFormulario rotulo="Responsável" id={`${id}-responsavel`}>
          <input
            id={`${id}-responsavel`}
            type="text"
            className={CLASSE_DO_CONTROLE}
            {...form.register('responsavel')}
          />
        </CampoDoFormulario>
      </LinhaDupla>
      <LinhaDupla>
        <CampoDoFormulario rotulo="Última atualização" id={`${id}-atualizacao`}>
          <input
            id={`${id}-atualizacao`}
            type="date"
            className={CLASSE_DO_CONTROLE}
            {...form.register('atualizacao')}
          />
        </CampoDoFormulario>
        <CampoDoFormulario rotulo="Custo (R$)" id={`${id}-custo`}>
          <input
            id={`${id}-custo`}
            type="number"
            step="0.01"
            className={CLASSE_DO_CONTROLE}
            {...form.register('custo', { setValueAs: custoDoCampo })}
          />
        </CampoDoFormulario>
      </LinhaDupla>
      <CampoDoFormulario rotulo="Observações" id={`${id}-observacao`}>
        <textarea
          id={`${id}-observacao`}
          className={CLASSE_DA_AREA_DE_TEXTO}
          {...form.register('observacao')}
        />
      </CampoDoFormulario>
    </CaixaDeDialogo>
  );
}
